/**
 * LeaveApplicationFormModal
 *
 * Shows the modern, single-page, branded leave application form PDF for one
 * application (LeaveApplicationFormPdfService on the backend) -- used by the
 * Reports → "Individual Application" tab so HR/employees can view or print a
 * prefilled copy of any application at any stage.
 *
 * Once the application is APPROVED:
 * - For every ordinary employee, the form is already finalized by the automatic Head of
 *   Department e-signature stamped onto it at approval time -- nothing further to do here.
 * - For the Managing Director's OWN leave only, HR instead sees the Chairman sign-off panel:
 *   print this same certificate, get the Chairman's physical countersignature, then upload
 *   the scan back here -- that upload IS the recorded confirmation, kept purely for
 *   audit/record-keeping (it never affects `status`). The backend enforces this scoping too.
 */
import { useEffect, useRef, useState } from "react";
import { Download, FileSignature, Loader2, Upload, X } from "lucide-react";

import { useAuth } from "#/context/AuthProvider";
import { useToast } from "#/components/common/ToastProvider";
import { leaveApi } from "../api";
import type { LeaveApplicationDetails } from "../types";

type Props = {
  applicationId: number;
  onClose: () => void;
};

export function LeaveApplicationFormModal({ applicationId, onClose }: Props) {
  const { hasRole, hasAnyRole, hasPermission } = useAuth();
  const canManageChairmanDoc =
    hasRole("SUPER_ADMIN") || hasAnyRole(["ADMIN"]) ||
    hasPermission("HR_LEAVE_MANAGE") || hasPermission("HR_LEAVE_APPROVE");

  const toast = useToast();
  const [details, setDetails] = useState<LeaveApplicationDetails | null>(null);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [downloadingSigned, setDownloadingSigned] = useState(false);
  const pdfUrlRef = useRef<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const [d, blob] = await Promise.all([
        leaveApi.getApplication(applicationId),
        leaveApi.applicationFormPdf(applicationId),
      ]);
      setDetails(d);
      if (pdfUrlRef.current) URL.revokeObjectURL(pdfUrlRef.current);
      const url = URL.createObjectURL(blob);
      pdfUrlRef.current = url;
      setPdfUrl(url);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load application form.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    return () => { if (pdfUrlRef.current) URL.revokeObjectURL(pdfUrlRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applicationId]);

  function downloadPdf() {
    if (!pdfUrl || !details) return;
    const a = document.createElement("a");
    a.href = pdfUrl;
    a.download = `${details.applicationNo || "leave-application"}.pdf`;
    a.click();
  }

  async function handleUploadSigned(file: File) {
    setUploading(true);
    try {
      const updated = await leaveApi.uploadChairmanDocument(applicationId, file);
      setDetails(updated);
      toast.success("Chairman-countersigned document saved.");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to upload the signed document.");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  }

  async function handleDownloadSigned() {
    setDownloadingSigned(true);
    try {
      const blob = await leaveApi.downloadChairmanDocument(applicationId);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = details?.chairmanDocumentOriginalName || `chairman-signed-${details?.applicationNo || applicationId}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to download the signed document.");
    } finally {
      setDownloadingSigned(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="relative flex w-full max-w-5xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl" style={{ maxHeight: "92vh" }}>
        <div className="flex shrink-0 items-center justify-between border-b bg-slate-50 px-5 py-3">
          <div className="text-sm font-black text-slate-800">
            Leave Application Form
            {details && <span className="ml-2 font-normal text-slate-400">#{details.applicationNo}</span>}
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={downloadPdf}
              disabled={!pdfUrl}
              className="inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-100 disabled:opacity-50"
            >
              <Download className="h-3.5 w-3.5" />
              Download PDF
            </button>
            <button type="button" onClick={onClose} className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700" title="Close">
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-auto bg-slate-100">
          {loading && (
            <div className="flex h-full min-h-[50vh] items-center justify-center text-slate-400">
              <Loader2 className="mr-2 h-5 w-5 animate-spin" /> Loading…
            </div>
          )}
          {error && (
            <div className="flex h-full min-h-[50vh] items-center justify-center text-sm font-semibold text-red-600">
              {error}
            </div>
          )}
          {!loading && !error && pdfUrl && (
            <iframe src={pdfUrl} title="Leave application form" className="h-full min-h-[70vh] w-full border-0" />
          )}
        </div>

        {!loading && !error && details && details.status === "APPROVED" && canManageChairmanDoc
          && !details.applicantIsManagingDirector && (
          <div className="shrink-0 border-t bg-slate-50 px-5 py-3">
            <div className="flex items-center gap-2 text-xs font-semibold text-slate-500">
              <FileSignature className="h-3.5 w-3.5" />
              This application is finalized by the automatic Head of Department e-signature stamped on the
              form above — no Chairman sign-off upload applies here. That upload is only used for the
              Managing Director's own leave applications.
            </div>
          </div>
        )}

        {!loading && !error && details && details.status === "APPROVED" && canManageChairmanDoc
          && details.applicantIsManagingDirector && (
          <div className="shrink-0 border-t bg-indigo-50/60 px-5 py-3">
            <div className="mb-2 flex items-center gap-2 text-xs font-black uppercase tracking-wide text-indigo-700">
              <FileSignature className="h-3.5 w-3.5" />
              Chairman Sign-off Record
              <span className="font-normal normal-case text-indigo-400">— Managing Director's leave</span>
            </div>
            {details.chairmanDocumentUploaded ? (
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="text-xs text-slate-600">
                  Countersigned copy on file
                  {details.chairmanDocumentUploadedAt && (
                    <> — uploaded {new Date(details.chairmanDocumentUploadedAt).toLocaleString()}</>
                  )}
                  {details.chairmanDocumentUploadedByName && <> by {details.chairmanDocumentUploadedByName}</>}
                </div>
                <button
                  type="button"
                  onClick={handleDownloadSigned}
                  disabled={downloadingSigned}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-indigo-200 bg-white px-3 py-1.5 text-xs font-semibold text-indigo-700 hover:bg-indigo-100 disabled:opacity-50"
                >
                  {downloadingSigned ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
                  Download signed copy
                </button>
              </div>
            ) : (
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="text-xs text-slate-600">
                  Print the certificate above for the Chairman's physical countersignature, then upload the
                  scanned copy here — this record is for audit purposes and does not change the application's status.
                </p>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="application/pdf,image/*"
                  className="hidden"
                  onChange={(e) => { const f = e.target.files?.[0]; if (f) handleUploadSigned(f); }}
                />
                <button
                  type="button"
                  disabled={uploading}
                  onClick={() => fileInputRef.current?.click()}
                  className="inline-flex items-center gap-1.5 rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-indigo-700 disabled:opacity-50"
                >
                  {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
                  Upload signed copy
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
